import {Box, Container, Typography} from "@mui/material";
import Toolbar from "@mui/material/Toolbar";
import React, {useEffect, useRef, useState} from "react";
import {PageGridPlacement as CmPageGridPlacement} from "../../../../packages/graphql-layer/src";
import Link from "next/link";
import {useTheme} from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import QuickAccessButton from "./QuickAccessButton";
import {toolbarSx} from "./LogoOnlyNavigation";
import Logo from "./Logo";
import {isPointInPolygon} from 'geolib';
import useWindowSize from "../../../utility/useWindowSize";
import {usePageContext} from "../../../context/PageContext";
import {calculateMouseSafeArea, Triangle} from "../../../utility/navigationUtil";
import {
    arrowBoxSx,
    desktopMenuSx,
    iconButtonSx,
    mobileMenuButtonSx,
    muiAppBarSx,
    quickAccessButtonSx,
    toolbarButtonHoveredSx,
    toolbarButtonSx
} from "./navigationStyles";
import {
    handleMenuItemBlur,
    handleArrowKeyDown,
    toggleSubmenu,
    handleMenuItemKeyboardNav
} from './keyboardNavigationHandlers';
import dynamic from "next/dynamic";


interface Props {
    navigation: any,
    page: any,
    header: CmPageGridPlacement,
    skipLinksVisible?: boolean
}


const AppBarMenu = dynamic(() => import('./AppBarMenu'), {
    ssr: false,
});

const AppBarMenuMobile = dynamic(() => import('./AppBarMenuMobile'), {
    ssr: false,
});

function Navigation(props: Props) {
    const theme = useTheme();
    const navigationRoot = props.navigation.content.site.root;
    const appBarRef = useRef(null);
    const popoverRef = useRef(null);
    const {greaterThanWindowLarge, lowerThanWindowLarge} = useWindowSize();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [visibleArrow, setVisibleArrow] = useState<number | null>(null);
    const [hoverState, setHoverState] = useState<string | null>(null);
    const [focusedMenuItemIndex, setFocusedMenuItemIndex] = useState<number | null>(null);
    const lastFocusedMenuItemIndex = useRef<number | null>(null);
    const [activeMenuItem, setActiveMenuItem] = useState<any>([]);
    const arrowButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const menuItemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
    const firstPopoverItemRef = useRef<HTMLButtonElement | HTMLDivElement | null>(null);
    const [deepMenu, setDeepMenu] = React.useState([]);
    const [mouseSafeArea, setMouseSafeArea] = React.useState<Triangle | null>(null);
    const {hideTopNavi} = usePageContext();
    const [isKeyboardNavigation, setIsKeyboardNavigation] = useState(true);

    useEffect(() => {
        // Invalidate mouse safe area after timeout
        if (mouseSafeArea !== null) {
            const timeoutId = setTimeout(() => {
                setMouseSafeArea(null);
            }, 1000);
            return () => clearTimeout(timeoutId);
        }
    }, [mouseSafeArea]);

    useEffect(() => {
        if (!isKeyboardNavigation) {
            document.activeElement instanceof HTMLElement && document.activeElement.blur();
        }
    }, [isKeyboardNavigation]);

    const isMouseInSafeArea = (event: any): boolean => {
        if (!mouseSafeArea) {
            return false;
        }
        const mousePositionX = event.clientX;
        const mousePositionY = event.clientY;
        return isPointInPolygon(
            {latitude: mousePositionY, longitude: mousePositionX},
            mouseSafeArea.map(vertex => ({
                latitude: vertex.y,
                longitude: vertex.x
            }))
        );
    }

    const handleMenuItemHover = (event: any, item: any) => {
        setIsKeyboardNavigation(false);
        if (isMouseInSafeArea(event)) {
            return;
        }
        setHoverState(item.id);
        if (item.children.length > 0) {
            setIsMenuOpen(true);
            setActiveMenuItem(item);
            setDeepMenu([]);
            calculateMouseSafeArea(event, setMouseSafeArea);
        } else if (item.children.length === 0) {
            setIsMenuOpen(false);
            setMouseSafeArea(null);
        } else {
            closeMenu(null);
        }
    }

    const clearHoverState = () => {
        setHoverState(null);
    }

    const handleMenuMouseLeave = (event: any) => {
        clearHoverState();
        closeMenu(event);
    };

    const handleMouseMove = (event: any, item: any) => {
        setIsKeyboardNavigation(false)
        if (hoverState === item.id || isMouseInSafeArea(event)) {
            return;
        }
        handleMenuItemHover(event, item);
    }

    const closeMenu = (event: any) => {
        if (!event
            || (event.relatedTarget !== appBarRef.current
                && event.relatedTarget !== popoverRef.current)
        ) {
            setIsMenuOpen(false);
            setMouseSafeArea(null);
            setHoverState(null);
        }
    }

    const handleHovered = (menuItem: any, index: number) => {
        const isMenuOpenAndActive = isMenuOpen && activeMenuItem?.id === menuItem?.id;

        return (hoverState === menuItem?.id
            || focusedMenuItemIndex === index
            || isMenuOpenAndActive
            || props.page?.selfUrl?.startsWith(menuItem?.selfUrl))
            ? toolbarButtonHoveredSx
            : toolbarButtonSx;
    }

    const setDeepMenuItems = (menuItem: any) => {
        setDeepMenu(menuItem.children ? menuItem.children : []);
    }

    const nextMainNavItemRef =
        lastFocusedMenuItemIndex.current !== null &&
        menuItemRefs.current[lastFocusedMenuItemIndex.current + 1]
            ? menuItemRefs.current[lastFocusedMenuItemIndex.current + 1]
            : null;

    const prevMainNavItemRef =
        lastFocusedMenuItemIndex.current !== null &&
        menuItemRefs.current[lastFocusedMenuItemIndex.current]
            ? menuItemRefs.current[lastFocusedMenuItemIndex.current]
            : null;

    const handleMouseEnter = () => {
        setIsKeyboardNavigation(false);
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    };

    return (
        <MuiAppBar id="navigation" data-cypress={"appBar"} sx={muiAppBarSx(hideTopNavi, greaterThanWindowLarge)}>
            <Container
                maxWidth={false}
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                }}
            >
                <Toolbar disableGutters sx={toolbarSx}>
                    {/*--- Logo ---*/}
                    <Logo header={props.header} navigation={props.navigation}/>

                    {/*--- Desktop Menu ---*/}
                    {greaterThanWindowLarge && (
                        <Box sx={desktopMenuSx} ref={appBarRef} onMouseLeave={closeMenu}>

                            {navigationRoot.children.map((menuItem: any, index: number) => (
                                <Box
                                    key={menuItem.id}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <Link
                                        data-cypress={"mainNavigationLink"}
                                        id={`btn-${menuItem.id}`}
                                        href={menuItem.selfUrl}
                                        passHref
                                        onClick={closeMenu}
                                        ref={(el: HTMLAnchorElement | null) => {
                                            menuItemRefs.current[index] = el;
                                        }}
                                        onMouseOver={(event) => handleMenuItemHover(event, menuItem)}
                                        onMouseMove={(event) => handleMouseMove(event, menuItem)}
                                        onMouseEnter={() => handleMouseEnter()}
                                        onFocus={() => setFocusedMenuItemIndex(index)}
                                        onBlur={(event) => handleMenuItemBlur(event, index, arrowButtonRefs, setFocusedMenuItemIndex)}
                                        onKeyDown={(event) => handleMenuItemKeyboardNav(event, index, navigationRoot, setIsKeyboardNavigation, arrowButtonRefs, lastFocusedMenuItemIndex)}
                                    >
                                        <Box
                                            sx={{
                                                display: "block",
                                                textDecoration: "none",
                                                padding: "0 16px",
                                                color: "inherit",
                                            }}
                                        >
                                            <Typography
                                                data-cypress={`mainNavigationSpan${index}`}
                                                variant={"navigationItem"}
                                                sx={handleHovered(menuItem, index)}
                                            >
                                                {menuItem.teaserTitle}
                                            </Typography>
                                        </Box>
                                    </Link>

                                    {menuItem.children?.length > 0 && (
                                        <IconButton
                                            tabIndex={isKeyboardNavigation ? 0 : -1}
                                            ref={(el) => (arrowButtonRefs.current[index] = el)}
                                            aria-label={`Toggle submenu for ${menuItem.teaserTitle}`}
                                            onFocus={() => setVisibleArrow(index)}
                                            onBlur={(event) => {
                                                setVisibleArrow(null);
                                                if (!isKeyboardNavigation) {
                                                    event.currentTarget.blur();
                                                }
                                            }}
                                            onKeyDown={(event) => handleArrowKeyDown(event, index, menuItem, menuItemRefs, isMenuOpen, firstPopoverItemRef, setIsMenuOpen, setActiveMenuItem, setFocusedMenuItemIndex, activeMenuItem)}
                                            onClick={() => toggleSubmenu(menuItem, index, activeMenuItem, setActiveMenuItem, setIsMenuOpen, setFocusedMenuItemIndex, setIsKeyboardNavigation)}
                                            sx={iconButtonSx(
                                                isKeyboardNavigation && visibleArrow === index,
                                                isMenuOpen && activeMenuItem === menuItem,
                                                theme
                                            )}
                                        >
                                            <Box
                                                sx={arrowBoxSx(
                                                    isMenuOpen && activeMenuItem === menuItem,
                                                    theme
                                                )}
                                            />
                                        </IconButton>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/*--- Quick Access Button ---*/}
                    <Box sx={quickAccessButtonSx}>
                        <QuickAccessButton buttonStyle={{float: "right"}} fullWidth={false} page={props.page}/>
                    </Box>

                    {/*--- Mobile Menu ---*/}
                    {lowerThanWindowLarge && (
                        <Box
                            sx={{
                                display: {xs: "flex", lg: "none"},
                            }}
                        >
                            <IconButton
                                data-cypress={"mobileMenuButton"}
                                sx={mobileMenuButtonSx}
                                size="large"
                                aria-label="menu"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={() => setIsMenuOpen((prev) => !prev)}
                                color="inherit"
                            >
                                <MenuIcon data-cypress={"mobileMenuIcon"}/>
                            </IconButton>
                            {isMenuOpen && (
                                <AppBarMenuMobile
                                    page={props.page}
                                    items={navigationRoot.children}
                                    open={isMenuOpen}
                                    header={props.page?.grid.placements.find(
                                        (p: CmPageGridPlacement) => p.name === "header"
                                    )}
                                    onClose={() => setIsMenuOpen(false)}
                                />
                            )}
                        </Box>
                    )}
                </Toolbar>
                {/*--- AppBarMenu ---*/}
                {isMenuOpen && greaterThanWindowLarge && (
                    <AppBarMenu
                        closeMenu={() => setIsMenuOpen(false)}
                        page={props.page}
                        isMenuOpen
                        selection={activeMenuItem}
                        deepMenu={deepMenu || []}
                        popoverRef={popoverRef}
                        firstPopoverItemRef={firstPopoverItemRef}
                        prevMainNavItemRef={prevMainNavItemRef}
                        nextMainNavItemRef={nextMainNavItemRef}
                        handleClose={handleMenuMouseLeave}
                        handleDeepMenu={setDeepMenuItems}
                        hoveredTopMenuItemLeftPosition="50%"
                        isFlyoutSmall={props.page.settings.isFlyoutSmall}
                    />
                )}
            </Container>
        </MuiAppBar>
    );
}

export default Navigation;